import type { IBlotterPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import type { BlotterItemDetails } from 'models/blotters/BlotterItemDetails';
import type {
  ActivityFilter,
  DateFilter,
  IBlotterColumnDefinition,
  SortDirection,
} from 'state/blotters/common/blotter.model';
import type { Fee, FICCGreek, GreeksTable, Metric } from 'state/quotes/details/details.model';
import type { ApiError } from 'utils/http/apiErrorHelper';

export interface IPriceImprovementBlotterItem {
  id: string /* Request for price improvement ID*/;
  quoteId: string;
  pricerId: string;
  referenceQuoteId: string;
  salesDesk: string;
  requester: string;
  salesComment?: string;
  status: string;
  targetTradingPrice: number;
  tradingPrice: number;
  details: BlotterItemDetails;
  underlyings: string;
}

export interface IPriceImprovementBlotterFilters {
  quickSearchTerm?: string;
  dateFilter: DateFilter;
  activityFilter: ActivityFilter;
  statusFilter?: string;
  advancedFilters: boolean;
  startDate?: string;
  endDate?: string;
}

interface IPriceImprovementBlotterDetail {
  item: IPriceImprovementBlotterItem;
  sensitivity?: QuoteSensitivity;
  sensitivityError?: string;
}

export type AssetClass = 'Credit' | 'Forex' | 'Rate' | 'Equity';

export interface QuoteSensitivity {
  assetClass: AssetClass;
  greeks?: GreeksTable;
  fees?: readonly Fee[];

  FICCGreeks?: readonly FICCGreek[];
  metrics?: readonly Metric[];
}

export interface IPriceImprovementBlotter extends IPriceImprovementBlotterFilters {
  blotterPreferenceState: IBlotterPreferenceState;
  columnDefinition: readonly IBlotterColumnDefinition<IPriceImprovementBlotterItem>[];
  error: boolean;
  acknowledgementError?: ApiError;
  selectedRowIds: readonly string[];
  items?: readonly IPriceImprovementBlotterItem[];
  loading: boolean;
  page: number;
  sort?: string;
  sortDir?: SortDirection;
  totalItems?: number;
  totalPages?: number;
  detail?: IPriceImprovementBlotterDetail;
  areBlotterPreferencesInError: boolean;
  canSaveBlotterPreferences: boolean;
}

export type IPriceImprovementBlotterFiltersWithMandatoryDates = Omit<
  IPriceImprovementBlotterFilters,
  'startDate' | 'endDateDate'
> & {
  startDate: string;
  endDate: string;
};

export type PriceImprovementBlotterState = Readonly<IPriceImprovementBlotter>;

export const initialPriceImprovementBlotterFilters: IPriceImprovementBlotterFilters = {
  activityFilter: 'mySelf',
  dateFilter: 'today',
  advancedFilters: false,
};

export const initialPriceImprovementBlotterState: PriceImprovementBlotterState = {
  ...initialPriceImprovementBlotterFilters,
  page: 1,
  loading: false,
  error: false,
  areBlotterPreferencesInError: false,
  canSaveBlotterPreferences: false,
  selectedRowIds: [],
  blotterPreferenceState: {
    columnState: [],
    pageSizeState: 25,
  },
  columnDefinition: [],
};
